import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import IconButton from "@mui/material/IconButton";
import { createUseStyles } from "react-jss";

const MAX_DOTS = 5;

const getVisiblePages = (page: number, count: number): number[] => {
  if (count <= MAX_DOTS) {
    return Array.from({ length: count }, (_, index) => index + 1);
  }

  let start = Math.max(1, page - Math.floor(MAX_DOTS / 2));
  const end = Math.min(count, start + MAX_DOTS - 1);
  start = Math.max(1, end - MAX_DOTS + 1);

  return Array.from({ length: end - start + 1 }, (_, index) => start + index);
};

const useStyles = createUseStyles({
  bar: {
    flexShrink: 0,
    width: "100%",
    backgroundColor: "#1e2229",
    borderTop: "1px solid #404754",
    padding: "8px 12px 10px",
    boxSizing: "border-box"
  },
  barFixed: {
    position: "fixed",
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 9,
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    padding: "10px 16px 12px",
    paddingBottom: "max(12px, env(safe-area-inset-bottom))",
    boxShadow: "0 -4px 16px rgba(0, 0, 0, 0.28)"
  },
  pageLabel: {
    textAlign: "center",
    fontSize: 13,
    color: "#94a3b8",
    margin: "0 0 8px"
  },
  row: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 12
  },
  navBtn: {
    color: "#e2e8f0 !important",
    border: "1px solid #404754 !important",
    backgroundColor: "#2c3139 !important",
    width: "44px !important",
    height: "44px !important",
    borderRadius: "10px !important",
    "&.Mui-disabled": {
      color: "#475569 !important",
      borderColor: "#2c3139 !important",
      backgroundColor: "transparent !important"
    }
  },
  dots: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 2,
    minWidth: 72
  },
  dotHit: {
    width: 28,
    height: 28,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    border: "none",
    padding: 0,
    background: "transparent",
    cursor: "pointer"
  },
  dot: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    backgroundColor: "#475569",
    transition: "transform 0.15s ease, background-color 0.15s ease"
  },
  dotActive: {
    width: 10,
    height: 10,
    backgroundColor: "#185FA5"
  }
});

const MobileBrowsePagination = ({
  page,
  count,
  cardRangeStart,
  cardRangeEnd,
  onPrevious,
  onNext,
  onChange,
  stacked = false
}: {
  page: number;
  count: number;
  cardRangeStart?: number;
  cardRangeEnd?: number;
  onPrevious: () => void;
  onNext: () => void;
  onChange: (page: number) => void;
  stacked?: boolean;
}) => {
  const classes = useStyles();
  const visiblePages = getVisiblePages(page, count);
  const hasCardRange =
    cardRangeStart !== undefined &&
    cardRangeEnd !== undefined &&
    cardRangeStart > 0 &&
    cardRangeEnd >= cardRangeStart;
  const pageLabel = hasCardRange
    ? `Page ${page} of ${count} (${cardRangeStart}-${cardRangeEnd})`
    : `Page ${page} of ${count}`;

  return (
    <footer className={`${classes.bar} ${stacked ? "" : classes.barFixed}`} aria-label="Page navigation">
      <p className={classes.pageLabel}>{pageLabel}</p>
      <div className={classes.row}>
        <IconButton
          className={classes.navBtn}
          aria-label="Previous page"
          disabled={page <= 1}
          onClick={onPrevious}>
          <ChevronLeftIcon />
        </IconButton>
        <div className={classes.dots} role="tablist" aria-label="Pages">
          {visiblePages.map((pageNumber) => (
            <button
              key={pageNumber}
              type="button"
              role="tab"
              aria-label={`Page ${pageNumber}`}
              aria-selected={pageNumber === page}
              className={classes.dotHit}
              onClick={() => onChange(pageNumber)}>
              <span
                className={`${classes.dot} ${pageNumber === page ? classes.dotActive : ""}`}
                aria-hidden
              />
            </button>
          ))}
        </div>
        <IconButton
          className={classes.navBtn}
          aria-label="Next page"
          disabled={page >= count}
          onClick={onNext}>
          <ChevronRightIcon />
        </IconButton>
      </div>
    </footer>
  );
};

export default MobileBrowsePagination;
